import { Eventified } from "./eventified.js";
import type {
	HookFn,
	HookifiedOptions,
	IHook,
	IWaterfallHook,
	OnHookOptions,
	PrependHookOptions,
	WaterfallHookContext,
	WaterfallHookFn,
	WaterfallHookResult,
} from "./types.js";

export type {
	HookFn,
	HookifiedOptions,
	IHook,
	IWaterfallHook,
	OnHookOptions,
	PrependHookOptions,
	WaterfallHookContext,
	WaterfallHookFn,
	WaterfallHookResult,
};

export class Hookified extends Eventified {
	private readonly _hooks: Map<string, IHook[]>;
	private _throwOnHookError = false;
	private _enforceBeforeAfter = false;
	private _deprecatedHooks: Map<string, string>;
	private _allowDeprecated = true;
	private _useHookClone = true;

	constructor(options?: HookifiedOptions) {
		super({
			eventLogger: options?.eventLogger,
			throwOnEmitError: options?.throwOnEmitError,
			throwOnEmptyListeners: options?.throwOnEmptyListeners,
		});
		this._hooks = new Map();
		this._deprecatedHooks = options?.deprecatedHooks
			? new Map(options.deprecatedHooks)
			: new Map();

		if (options?.throwOnHookError !== undefined) {
			this._throwOnHookError = options.throwOnHookError;
		}

		if (options?.enforceBeforeAfter !== undefined) {
			this._enforceBeforeAfter = options.enforceBeforeAfter;
		}

		if (options?.allowDeprecated !== undefined) {
			this._allowDeprecated = options.allowDeprecated;
		}

		if (options?.useHookClone !== undefined) {
			this._useHookClone = options.useHookClone;
		}
	}

	/**
	 * Gets all hooks
	 * @returns {Map<string, IHook[]>}
	 */
	public get hooks() {
		return this._hooks;
	}

	/**
	 * Gets whether an error should be thrown when a hook throws an error. Default is false and only emits an error event.
	 * @returns {boolean}
	 */
	public get throwOnHookError() {
		return this._throwOnHookError;
	}

	/**
	 * Sets whether an error should be thrown when a hook throws an error. Default is false and only emits an error event.
	 * @param {boolean} value
	 */
	public set throwOnHookError(value) {
		this._throwOnHookError = value;
	}

	/**
	 * Gets whether to enforce that all hook names start with 'before' or 'after'. Default is false.
	 * @returns {boolean}
	 * @default false
	 */
	public get enforceBeforeAfter() {
		return this._enforceBeforeAfter;
	}

	/**
	 * Sets whether to enforce that all hook names start with 'before' or 'after'. Default is false.
	 * @param {boolean} value
	 */
	public set enforceBeforeAfter(value) {
		this._enforceBeforeAfter = value;
	}

	/**
	 * Gets the map of deprecated hook names to deprecation messages.
	 * @returns {Map<string, string>}
	 */
	public get deprecatedHooks() {
		return this._deprecatedHooks;
	}

	/**
	 * Sets the map of deprecated hook names to deprecation messages.
	 * @param {Map<string, string>} value
	 */
	public set deprecatedHooks(value) {
		this._deprecatedHooks = value;
	}

	/**
	 * Gets whether deprecated hooks are allowed to be registered and executed. Default is true.
	 * @returns {boolean}
	 */
	public get allowDeprecated() {
		return this._allowDeprecated;
	}

	/**
	 * Sets whether deprecated hooks are allowed to be registered and executed. Default is true.
	 * @param {boolean} value
	 */
	public set allowDeprecated(value) {
		this._allowDeprecated = value;
	}

	/**
	 * Gets whether hook objects are cloned before storing. Default is true.
	 * @returns {boolean}
	 */
	public get useHookClone() {
		return this._useHookClone;
	}

	/**
	 * Sets whether hook objects are cloned before storing. Default is true.
	 * When false, the original IHook reference is stored directly.
	 * @param {boolean} value
	 */
	public set useHookClone(value) {
		this._useHookClone = value;
	}

	/**
	 * Adds a handler function for a specific event.
	 * Supports both object and legacy signatures:
	 *   onHook({ event, handler }, options?)
	 *   onHook(event, handler)
	 * To register multiple hooks at once, use {@link onHooks}.
	 * @param {IHook | string} hookOrEvent - the hook object or event name
	 * @param {OnHookOptions | HookFn} [optionsOrHandler] - options (when using object form) or handler function (when using string form)
	 * @returns {IHook | undefined} the stored hook, or undefined if blocked by deprecation
	 */
	public onHook(hook: IHook, options?: OnHookOptions): IHook | undefined;
	public onHook(event: string, handler: HookFn): IHook | undefined;
	public onHook(
		hookOrEvent: IHook | string,
		optionsOrHandler?: OnHookOptions | HookFn,
	): IHook | undefined {
		let hook: IHook;
		let options: OnHookOptions | undefined;

		if (typeof hookOrEvent === "string") {
			if (typeof optionsOrHandler !== "function") {
				throw new TypeError(
					"When calling onHook(event, handler), the second argument must be a function",
				);
			}

			hook = { event: hookOrEvent, handler: optionsOrHandler };
			options = undefined;
		} else {
			hook = hookOrEvent;
			options = optionsOrHandler as OnHookOptions | undefined;
		}
		this.validateHookName(hook.event);
		if (!this.checkDeprecatedHook(hook.event)) {
			return undefined; // Skip registration if deprecated hooks are not allowed
		}

		const shouldClone = options?.useHookClone ?? this._useHookClone;
		const entry: IHook = shouldClone
			? { id: hook.id, event: hook.event, handler: hook.handler }
			: hook;

		entry.id = entry.id ?? crypto.randomUUID();

		const eventHandlers = this._hooks.get(hook.event);
		if (eventHandlers) {
			// Check for duplicate id — replace in-place if found
			const existingIndex = eventHandlers.findIndex((h) => h.id === entry.id);
			if (existingIndex !== -1) {
				eventHandlers[existingIndex] = entry;
			} else {
				const position = options?.position ?? "Bottom";
				if (position === "Top") {
					eventHandlers.unshift(entry);
				} else if (position === "Bottom") {
					eventHandlers.push(entry);
				} else {
					const index = Math.max(0, Math.min(position, eventHandlers.length));
					eventHandlers.splice(index, 0, entry);
				}
			}
		} else {
			this._hooks.set(hook.event, [entry]);
		}

		return entry;
	}

	/**
	 * Alias for onHook. This is provided for compatibility with other libraries that use the `addHook` method.
	 * @param {string} event - the event name
	 * @param {HookFn} handler - the handler function
	 * @returns {void}
	 */
	public addHook(event: string, handler: HookFn) {
		this.onHook({ event, handler });
	}

	/**
	 * Adds handler functions for specific events
	 * @param {Array<IHook>} hooks
	 * @param {OnHookOptions} [options] - optional per-call options (e.g., useHookClone override, position)
	 * @returns {void}
	 */
	public onHooks(hooks: IHook[], options?: OnHookOptions) {
		for (const hook of hooks) {
			this.onHook(hook, options);
		}
	}

	/**
	 * Adds a handler function for a specific event that runs before all other handlers.
	 * Equivalent to calling `onHook(hook, { position: "Top" })`.
	 * @param {IHook} hook - the hook containing event name and handler
	 * @param {PrependHookOptions} [options] - optional per-call options (e.g., useHookClone override)
	 * @returns {IHook | undefined} the stored hook, or undefined if blocked by deprecation
	 */
	public prependHook(
		hook: IHook,
		options?: PrependHookOptions,
	): IHook | undefined {
		return this.onHook(hook, { ...options, position: "Top" });
	}

	/**
	 * Adds a handler that only executes once for a specific event before all other handlers.
	 * Equivalent to calling `onHook` with a self-removing wrapper and `{ position: "Top" }`.
	 * @param {IHook} hook - the hook containing event name and handler
	 * @param {PrependHookOptions} [options] - optional per-call options (e.g., useHookClone override)
	 * @returns {IHook | undefined} the stored hook, or undefined if blocked by deprecation
	 */
	public prependOnceHook(
		hook: IHook,
		options?: PrependHookOptions,
	): IHook | undefined {
		// biome-ignore lint/suspicious/noExplicitAny: this is for any parameter compatibility
		const wrappedHandler = async (...arguments_: any[]) => {
			this.removeHook({ event: hook.event, handler: wrappedHandler });
			return hook.handler(...arguments_);
		};

		// Keep a reference to the original handler so removeHook() can remove the
		// once wrapper when called with the original handler reference.
		// biome-ignore lint/suspicious/noExplicitAny: attaching metadata to the wrapper
		(wrappedHandler as any)._originalHandler = hook.handler;

		return this.onHook(
			{ id: hook.id, event: hook.event, handler: wrappedHandler },
			{ ...options, position: "Top" },
		);
	}

	/**
	 * Adds a handler that only executes once for a specific event
	 * @param {IHook} hook - the hook containing event name and handler
	 */
	public onceHook(hook: IHook) {
		this.validateHookName(hook.event);
		if (!this.checkDeprecatedHook(hook.event)) {
			return; // Skip registration if deprecated hooks are not allowed
		}
		// biome-ignore lint/suspicious/noExplicitAny: this is for any parameter compatibility
		const wrappedHandler = async (...arguments_: any[]) => {
			this.removeHook({ event: hook.event, handler: wrappedHandler });
			return hook.handler(...arguments_);
		};

		// Keep a reference to the original handler so removeHook() can remove the
		// once wrapper when called with the original handler reference.
		// biome-ignore lint/suspicious/noExplicitAny: attaching metadata to the wrapper
		(wrappedHandler as any)._originalHandler = hook.handler;

		this.onHook({ id: hook.id, event: hook.event, handler: wrappedHandler });
	}

	/**
	 * Removes a handler function for a specific event
	 * @param {IHook} hook - the hook containing event name and handler to remove
	 * @returns {IHook | undefined} the removed hook, or undefined if not found
	 */
	public removeHook(hook: IHook): IHook | undefined {
		this.validateHookName(hook.event);
		const eventHandlers = this._hooks.get(hook.event);
		if (eventHandlers) {
			// Prefer an exact handler match before falling back to once-wrapper
			// matching, so a normal hook is never left behind when an identical
			// once-wrapper happens to precede it in the array.
			let index = eventHandlers.findIndex((h) => h.handler === hook.handler);
			if (index === -1) {
				index = eventHandlers.findIndex((h) => {
					// Match once-wrappers by their original handler reference. The
					// undefined guard prevents a normal hook (whose _originalHandler
					// is also undefined) from matching when hook.handler is undefined.
					// biome-ignore lint/suspicious/noExplicitAny: wrapper metadata lookup
					const original = (h.handler as any)._originalHandler;
					return original !== undefined && original === hook.handler;
				});
			}
			if (index !== -1) {
				eventHandlers.splice(index, 1);
				if (eventHandlers.length === 0) {
					this._hooks.delete(hook.event);
				}

				return { event: hook.event, handler: hook.handler };
			}
		}

		return undefined;
	}

	/**
	 * Removes multiple hook handlers
	 * @param {Array<IHook>} hooks
	 * @returns {IHook[]} the hooks that were successfully removed
	 */
	public removeHooks(hooks: IHook[]): IHook[] {
		const removed: IHook[] = [];
		for (const hook of hooks) {
			const result = this.removeHook(hook);
			if (result) {
				removed.push(result);
			}
		}

		return removed;
	}

	/**
	 * Calls all handlers for a specific event
	 * @param {string} event
	 * @param {T[]} arguments_
	 * @returns {Promise<void>}
	 */
	public async hook<T>(event: string, ...arguments_: T[]) {
		this.validateHookName(event);
		if (!this.checkDeprecatedHook(event)) {
			return; // Skip execution if deprecated hooks are not allowed
		}
		const eventHandlers = this._hooks.get(event);
		if (eventHandlers) {
			for (const hook of [...eventHandlers]) {
				try {
					await hook.handler(...arguments_);
				} catch (error) {
					const message = `${event}: ${(error as Error).message}`;
					this.emit("error", new Error(message));

					if (this._throwOnHookError) {
						throw new Error(message);
					}
				}
			}
		}
	}

	/**
	 * Calls all synchronous handlers for a specific event.
	 * Async handlers (declared with `async` keyword) are silently skipped.
	 *
	 * Note: The `hook` method is preferred as it executes both sync and async functions.
	 * Use `hookSync` only when you specifically need synchronous execution.
	 * @param {string} event
	 * @param {T[]} arguments_
	 * @returns {void}
	 */
	public hookSync<T>(event: string, ...arguments_: T[]): void {
		this.validateHookName(event);
		if (!this.checkDeprecatedHook(event)) {
			return;
		}

		const eventHandlers = this._hooks.get(event);
		if (eventHandlers) {
			for (const hook of [...eventHandlers]) {
				// Skip async functions silently
				if (hook.handler.constructor.name === "AsyncFunction") {
					continue;
				}

				try {
					hook.handler(...arguments_);
				} catch (error) {
					const message = `${event}: ${(error as Error).message}`;
					this.emit("error", new Error(message));

					if (this._throwOnHookError) {
						throw new Error(message);
					}
				}
			}
		}
	}

	/**
	 * Prepends the word `before` to your hook. Example is event is `test`, the before hook is `before:test`.
	 * @param {string} event - The event name
	 * @param {T[]} arguments_ - The arguments to pass to the hook
	 */
	public async beforeHook<T>(event: string, ...arguments_: T[]) {
		await this.hook(`before:${event}`, ...arguments_);
	}

	/**
	 * Prepends the word `after` to your hook. Example is event is `test`, the after hook is `after:test`.
	 * @param {string} event - The event name
	 * @param {T[]} arguments_ - The arguments to pass to the hook
	 */
	public async afterHook<T>(event: string, ...arguments_: T[]) {
		await this.hook(`after:${event}`, ...arguments_);
	}

	/**
	 * Calls all handlers for a specific event. This is an alias for `hook` and is provided for
	 * compatibility with other libraries that use the `callHook` method.
	 * @param {string} event
	 * @param {T[]} arguments_
	 * @returns {Promise<void>}
	 */
	public async callHook<T>(event: string, ...arguments_: T[]) {
		await this.hook(event, ...arguments_);
	}

	/**
	 * Gets all hooks for a specific event
	 * @param {string} event
	 * @returns {IHook[]}
	 */
	public getHooks(event: string) {
		this.validateHookName(event);
		return this._hooks.get(event);
	}

	/**
	 * Gets a specific hook by id, searching across all events
	 * @param {string} id - the hook id
	 * @returns {IHook | undefined} the hook if found, or undefined
	 */
	public getHook(id: string): IHook | undefined {
		for (const eventHandlers of this._hooks.values()) {
			const found = eventHandlers.find((h) => h.id === id);
			if (found) {
				return found;
			}
		}

		return undefined;
	}

	/**
	 * Removes one or more hooks by id, searching across all events
	 * @param {string | string[]} id - the hook id or array of hook ids to remove
	 * @returns {IHook | IHook[] | undefined} the removed hook(s), or undefined/empty array if not found
	 */
	public removeHookById(id: string | string[]): IHook | IHook[] | undefined {
		if (Array.isArray(id)) {
			const removed: IHook[] = [];
			for (const singleId of id) {
				const result = this.removeHookById(singleId);
				if (result && !Array.isArray(result)) {
					removed.push(result);
				}
			}

			return removed;
		}

		for (const [event, eventHandlers] of this._hooks.entries()) {
			const index = eventHandlers.findIndex((h) => h.id === id);
			if (index !== -1) {
				const [removed] = eventHandlers.splice(index, 1);
				if (eventHandlers.length === 0) {
					this._hooks.delete(event);
				}

				return removed;
			}
		}

		return undefined;
	}

	/**
	 * Removes all hooks
	 * @returns {void}
	 */
	public clearHooks() {
		this._hooks.clear();
	}

	/**
	 * Removes all hooks for a specific event and returns the removed hooks.
	 * @param {string} event - The event name to remove hooks for.
	 * @returns {IHook[]} the hooks that were removed
	 */
	public removeEventHooks(event: string): IHook[] {
		this.validateHookName(event);
		const eventHandlers = this._hooks.get(event);
		if (eventHandlers) {
			const removed = [...eventHandlers];
			this._hooks.delete(event);
			return removed;
		}

		return [];
	}

	/**
	 * Validates hook event name if enforceBeforeAfter is enabled
	 * @param {string} event - The event name to validate
	 * @throws {Error} If enforceBeforeAfter is true and event doesn't start with 'before' or 'after'
	 */
	private validateHookName(event: string): void {
		if (this._enforceBeforeAfter) {
			const eventValue = event.trim().toLocaleLowerCase();
			if (!eventValue.startsWith("before") && !eventValue.startsWith("after")) {
				throw new Error(
					`Hook event "${event}" must start with "before" or "after" when enforceBeforeAfter is enabled`,
				);
			}
		}
	}

	/**
	 * Checks if a hook is deprecated and emits a warning if it is
	 * @param {string} event - The event name to check
	 * @returns {boolean} - Returns true if the hook should proceed, false if it should be blocked
	 */
	private checkDeprecatedHook(event: string): boolean {
		if (this._deprecatedHooks.has(event)) {
			const message = this._deprecatedHooks.get(event);
			const warningMessage = `Hook "${event}" is deprecated${message ? `: ${message}` : ""}`;

			// Emit deprecation warning event
			this.emit("warn", { hook: event, message: warningMessage });

			// Return false if deprecated hooks are not allowed
			return this._allowDeprecated;
		}
		return true;
	}
}

export { Eventified } from "./eventified.js";
export { Hook } from "./hooks/hook.js";
export { ParallelHook } from "./hooks/parallel-hook.js";
export { WaterfallHook } from "./hooks/waterfall-hook.js";
export type {
	EventEmitterOptions,
	EventListener,
	IEventEmitter,
	IParallelHook,
	Logger,
	ParallelHookContext,
	ParallelHookFinalContext,
	ParallelHookFinalFn,
	ParallelHookFn,
	ParallelHookResult,
} from "./types.js";
